import { useState, useEffect, useRef } from 'react';
import { startOfDay } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

interface CalendarFilters {
  selectedDate: string; // ISO string
  selectedWorkoutTypes: string[];
}

// Helper function to get organization-specific storage key
const getCalendarFiltersKey = (organizationId: string) => 
  `fitness_app_calendar_filters_${organizationId}`;

// Helper function to normalize dates to start of day in local timezone using date-fns
const normalizeDate = (date: Date) => {
  return startOfDay(date);
};

// Helper function to load initial filters from localStorage
const loadInitialFilters = (organizationId: string | undefined) => {
  if (!organizationId) {
    return {
      selectedDate: normalizeDate(new Date()),
      selectedWorkoutTypes: new Set<string>(['all'])
    };
  }

  try {
    const storageKey = getCalendarFiltersKey(organizationId);
    const savedFilters = localStorage.getItem(storageKey);
    console.log('Loading calendar filters from localStorage:', savedFilters);
    
    if (savedFilters) {
      const filters: CalendarFilters = JSON.parse(savedFilters);
      
      return {
        selectedDate: filters.selectedDate ? normalizeDate(new Date(filters.selectedDate)) : normalizeDate(new Date()),
        selectedWorkoutTypes: filters.selectedWorkoutTypes && filters.selectedWorkoutTypes.length > 0 
          ? new Set(filters.selectedWorkoutTypes) 
          : new Set<string>(['all']) // Default to 'all' when no specific types saved
      };
    }
  } catch (error) {
    console.error('Failed to load calendar filters from localStorage:', error);
  }
  
  // Return defaults if no saved filters or error
  return {
    selectedDate: normalizeDate(new Date()),
    selectedWorkoutTypes: new Set<string>(['all']) // Default to 'all'
  };
};

export const useCalendarFilters = () => {
  const { currentOrganization } = useAuth();
  const skipSaveRef = useRef(false);

  const initialFilters = loadInitialFilters(currentOrganization?.id);
  const [selectedDate, setSelectedDate] = useState<Date>(initialFilters.selectedDate);
  const [selectedWorkoutTypes, setSelectedWorkoutTypes] = useState<Set<string>>(initialFilters.selectedWorkoutTypes);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    console.log('skipSaveRef.current',skipSaveRef.current)
    if (!currentOrganization?.id) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }

    const filters: CalendarFilters = {
      selectedDate: selectedDate.toISOString(),
      selectedWorkoutTypes: Array.from(selectedWorkoutTypes),
    };
    
    const storageKey = getCalendarFiltersKey(currentOrganization.id);
    localStorage.setItem(storageKey, JSON.stringify(filters));
  }, [selectedDate, selectedWorkoutTypes, currentOrganization?.id]);

  // Reset filters when organization changes
  useEffect(() => {
    if (currentOrganization?.id) {
      // Prevent saving initial state for new org
      skipSaveRef.current = true;
      const newFilters = loadInitialFilters(currentOrganization.id);
      setSelectedDate(newFilters.selectedDate);
      setSelectedWorkoutTypes(newFilters.selectedWorkoutTypes);
    }
  }, [currentOrganization?.id]);

  return {
    selectedDate,
    setSelectedDate,
    selectedWorkoutTypes,
    setSelectedWorkoutTypes,
  };
};
